import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Box,
  CircularProgress,
  Divider,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import SearchIcon from "@mui/icons-material/Search";
import { useDeleteMultipleNotes } from "../../../../../../hooks/panelClient/useDeleteMultipleNotes";
import { fetchNotasDesdeAPI } from "../../../../../../hooks/useGetNotes";
import { toastAlert } from "../../../../../../utils/AlertContext";

const DeleteMultipleNotesModal = ({
  open,
  onClose,
  section,
  token,
  idDocument,
  selectedDate,
  onDeleteSuccess,
}) => {
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const { loading: deleting, deleteMultipleNotes } = useDeleteMultipleNotes(
    token,
    () => {
      onDeleteSuccess();
    }
  );

  useEffect(() => {
    if (open && section?.ID_SECCION && token && idDocument) {
      setSearchTerm("");
      setSelectedIds([]);
      const loadNotes = async () => {
        setLoadingNotes(true);
        try {
          const data = await fetchNotasDesdeAPI(
            token,
            selectedDate,
            section.ID_SECCION,
            idDocument
          );
          const sortedData = [...data]
            .map((item, index) => ({
              ...item,
              orden_editor:
                item.orden_editor !== undefined ? item.orden_editor : index,
            }))
            .sort((a, b) => a.orden_editor - b.orden_editor);
          setNotes(sortedData);
        } catch (error) {
          console.error("Error loading notes for delete modal:", error);
          toastAlert("Error al obtener las notas de la sección", "error");
        } finally {
          setLoadingNotes(false);
        }
      };
      loadNotes();
    } else {
      setNotes([]);
    }
  }, [open, section, token, idDocument, selectedDate]);

  const filteredNotes = notes.filter((note) =>
    note.titulo?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredNotes.map((note) => note.id_sintesis);
    const allFilteredSelected = filteredIds.every((id) =>
      selectedIds.includes(id)
    );

    if (allFilteredSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => {
        const uniqueIds = new Set([...prev, ...filteredIds]);
        return Array.from(uniqueIds);
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedIds.length === 0) return;
    const isSuccess = await deleteMultipleNotes(selectedIds);
    if (isSuccess) {
      onClose();
    }
  };

  const isAllFilteredSelected =
    filteredNotes.length > 0 &&
    filteredNotes.every((note) => selectedIds.includes(note.id_sintesis));

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)"
              : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >

      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 1,
          pt: 3,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: "error.light",
            color: "error.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <DeleteSweepIcon sx={{ fontSize: 24 }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, lineHeight: 1.2, color: "text.primary" }}
          >
            Eliminar Notas Múltiples
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Sección: <strong>{section?.NOMBRE_SECCION}</strong>
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={onClose}
          disabled={deleting}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {loadingNotes ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress color="error" />
            <Typography variant="body2" color="text.secondary">
              Cargando notas de la sección...
            </Typography>
          </Box>
        ) : notes.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }} color="text.secondary">
              Esta sección no contiene notas activas para eliminar.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mt: 0.5,
              }}
            >
              <TextField
                placeholder="Buscar nota por título..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: "250px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={handleSelectAllFiltered}
                  disabled={filteredNotes.length === 0}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    px: 1.5,
                  }}
                >
                  {isAllFilteredSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                </Button>

              </Box>
            </Box>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)",
                maxHeight: "350px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: (theme) => theme.palette.divider,
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
                },
              }}
            >
              {filteredNotes.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron notas que coincidan con la búsqueda.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredNotes.map((note, index) => {
                    const isSelected = selectedIds.includes(note.id_sintesis);
                    const labelId = `note-list-label-${note.id_sintesis}`;

                    return (
                      <React.Fragment key={note.id_sintesis}>
                        {index > 0 && <Divider />}
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => handleToggle(note.id_sintesis)}
                            dense
                            sx={{
                              py: 1.5,
                              px: 2,
                              transition: "background-color 0.2s ease",
                              backgroundColor: isSelected
                                ? (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(244, 67, 54, 0.08)"
                                      : "rgba(244, 67, 54, 0.04)"
                                : "transparent",
                              "&:hover": {
                                backgroundColor: isSelected
                                  ? (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "rgba(244, 67, 54, 0.12)"
                                        : "rgba(244, 67, 54, 0.08)"
                                  : undefined,
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Checkbox
                                edge="start"
                                checked={isSelected}
                                tabIndex={-1}
                                disableRipple
                                color="error"
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              id={labelId}
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: "text.primary",
                                    mb: 0.5,
                                  }}
                                >
                                  {note.titulo}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </>
        )}
      </DialogContent>

     <Divider />
      <p style={{ fontSize: "12px", color: "text.secondary", textAlign: "end", padding: "12px 24px 0px", fontStyle: "italic" }}>Nota: Esta acción es irreversible. </p>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={deleting}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteConfirm}
          disabled={selectedIds.length === 0 || deleting || loadingNotes}
          startIcon={
            deleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteSweepIcon />
            )
          }
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            boxShadow: (theme) =>
              selectedIds.length > 0 && !deleting
                ? "0 4px 12px rgba(244, 67, 54, 0.25)"
                : "none",
          }}
        >
          {deleting ? "Eliminando..." : `Eliminar notas (${selectedIds.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteMultipleNotesModal;
